import { SSR_TEST_QUERY_FIRED } from '@confluence/graphql/entry-points/SSRTestLink.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';

// Keep query listeners at loading state when GLOBAL_APOLLO_SKIP_QUERY is true.
// SSR runtime will cancel all the in-flight requests once the main function exits.

type BlockedQueryAction = 'pending_forever' | 'client_render';

const logBlockedQuery = (operationName: string, reason: string, action: BlockedQueryAction) => {
	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging' ||
		process.env.CLOUD_ENV === 'hello'
	) {
		// eslint-disable-next-line no-console
		console.warn(
			`Query blocked: ${operationName}. Reason: ${reason} SSR_QUERY_BLOCKED action=${action}`,
		);
	}
};

export const shouldBlockRequest = ({
	operationName,
	operationKind,
	blockedAction = 'pending_forever',
}: {
	operationName: string;
	operationKind?: string;
	blockedAction?: BlockedQueryAction;
}): boolean => {
	if (!window['GLOBAL_APOLLO_SKIP_QUERY']) {
		return false;
	}

	// experimental is not being used for Relay

	if (operationKind === 'subscription') {
		logBlockedQuery(operationName, 'Subscription', blockedAction);
		return true;
	}

	// Allow the query only if it is fired within a boundary (preloader).
	if (getQueryBoundary()) {
		return false;
	}

	logBlockedQuery(
		operationName,
		'No query boundary. Query fired outside preloaders after rendering started.',
		blockedAction,
	);
	return true;
};

// Keyed by window because SSR creates a window per request.
const blockedRequestsByWindow = new WeakMap<Window, Set<string>>();

export const markRequestBlocked = (requestIdentifier: string): void => {
	let blockedRequests = blockedRequestsByWindow.get(window);
	if (!blockedRequests) {
		blockedRequests = new Set();
		blockedRequestsByWindow.set(window, blockedRequests);
	}
	blockedRequests.add(requestIdentifier);
};

// An active request with a blocked identifier will never settle, so it must not be treated as a
// preload worth waiting for.
export const isRequestBlocked = (requestIdentifier: string): boolean =>
	blockedRequestsByWindow.get(window)?.has(requestIdentifier) ?? false;

export const getBlockedRequestCount = (): number => blockedRequestsByWindow.get(window)?.size ?? 0;

export const markSSRTestQueryFired = (operationName: string): void => {
	SSR_TEST_QUERY_FIRED[operationName] = (SSR_TEST_QUERY_FIRED[operationName] || 0) + 1;
};
